import React, { forwardRef, useEffect, useState, useMemo, useCallback } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _ from 'lodash';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';

const MendMappingForm = forwardRef(function MendMappingForm({disableProjectFields = false, ...props}, ref) {
  const [products, setProducts] = useState([]);
  const [productsList, setProductList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [initialValues] = useState(() => {
    if (props.record) {
      return undefined;
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
      },
    ];
  });

  const parseOrganisationsListing = (projectsListing) => {
    
    let productMapping = {};
    Object.keys(projectsListing).forEach(_project => {
      projectsListing[_project].forEach(project => {
        const { otherProperties: { productToken, productName } = {} } = project;
        productMapping = { ...productMapping, [productToken]: productName };
      }) 
    });
    setProductList(_.map(productMapping, (name, id) => ({ id, name })));
  };


  useEffect(() => {
    setLoading(true);
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      const product = _.groupBy(projects,'otherProperties.productName');
      parseOrganisationsListing(product)
      setProducts(product);
    }).catch(() => {
      message.error('Problem fetching projects');
    }).finally(() => setLoading(false));

    return cancel;
  }, [props.loginConfigId,props.toolName]);

  
  const getExtraFieldsData = useCallback((config) => {
    const { projectToken, productToken } = config;

    let projects = [];

    _.forEach(productToken, (productToken) => {
      const productName = productsList.find(product => product.id ===  productToken)?.name;
      projects = [ ...projects, ...(products[productName] || [])];
    });
    const productName = _.map(productToken, token => productsList.find(product => product.id ===  token)?.name).join(',');
    const projectName = _.map(projectToken, token => {
     const project = _.find(projects, (obj) => obj.otherProperties.projectToken === token);
     const prependProductName = `${project.otherProperties.productName}/${project.name}`;
     return prependProductName;
    }).join(',');
    
    const productProjectToken = _.flatMap(productToken, token => {
      const associatedProjectTokens = projects
        .filter(obj => obj.otherProperties.productToken === token && projectToken.includes(obj.otherProperties.projectToken))
        .map(p => p.otherProperties.projectToken)
    
      return associatedProjectTokens.length
        ? associatedProjectTokens.map(proToken => `${token}/${proToken}`)
        : token;
    });

    const projectTokenNameMap = projectToken.reduce((projectMap, projectToken) => {
      const project = _.find(projects, (obj) => obj.otherProperties.projectToken === projectToken);
      const prependProductName = `${project.otherProperties.productName}/${project.name}`;
      projectMap[projectToken] = prependProductName;
      return projectMap;
    },{})

    const productTokenNameMap = productToken.reduce((productMap, productToken) => {
      const productName = productsList.find(product => product.id === productToken)?.name;
      productMap[productToken] = productName;
      return productMap;
    },{})

    const tokenNameMap = {
      productTokenNameMap,
      projectTokenNameMap
    }
    
    return ({
      projectToken: projectToken.join(','),
      projectName,
      productToken: productToken.join(','),
      productName,
      productProjectToken: productProjectToken.join(','),
      tokenNameMap : JSON.stringify(tokenNameMap),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  },[products,productsList]);


  const changeProject = useCallback((value, field) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      projectToken: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);


  const getFilteredProjectOptions = useCallback((productTokens) => {
    if (_.isEmpty(products)) {
      return;
    }

    let filteredProjects = [];

    _.forEach(productTokens, (productToken) => {
      const productName = productsList.find(product => product.id ===  productToken)?.name;
      filteredProjects = [ ...filteredProjects, ...(products[productName] || [])];
    });

    const projectOptions = filteredProjects?.map(project =>   ({ label: project.name, value:project.otherProperties.projectToken }));
    return parseSelectOptions(projectOptions);

  }, [products,productsList]);

  const isProjectFieldDisabled = useCallback((fieldKey, form) => {
    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
    return (!fieldConfiguration || !fieldConfiguration.productToken);
  }, []);


  const extraFields = useMemo(() => ([
    {
      name: 'productToken',
      colSpan: '12',
      type: FieldTypes.MULTI_SELECT,
      label: 'Product Name',
      options: parseSelectOptions(productsList),
      disabled: disableProjectFields,
      rules: [{ required: true, message: `Product Name is required` }],
      props:{
        onChange: changeProject,
        autoFocus: true
      }
    },
    {
      name: 'projectToken',
      colSpan: '12',
      type: FieldTypes.MULTI_SELECT,
      label: 'Project Name',
      rules: [{ required: true, message: `Project Name is required` }],
      propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form) || disableProjectFields }),
      props: {
        getFormListOptions: (fieldKey) => {
          const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
          return getFilteredProjectOptions(fieldConfiguration?.productToken);
        },
      },
    }
  ]), [productsList, disableProjectFields, changeProject, isProjectFieldDisabled, form, getFilteredProjectOptions]);


  return (
    <GenericMappingForm
      form={form}
      toolLoading={loading}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      {...modifyProps(props)}
    />
  )
})

export default MendMappingForm;

const modifyProps = (props) => {
  if (!props.record) {
    return props;
  }

  return {
    ...props,
    record: {
      ...props.record,
      projectToken: props.record.projectToken?.split(',') || [],
      projectName: props.record.projectName?.split(',') || [],
      productToken: props.record.productToken?.split(',') || [],
      productName: props.record.productName?.split(',') || [],
    }
  }
};
